// Pure orchestration helpers for the Deploy All flow.
//
// Kept free of React / network so the state machine can be unit-tested in
// isolation. The provider (contexts/DeployAllContext) owns the effects and
// wires these together.

export type RepoState =
  | 'pending'
  | 'dispatching'
  | 'queued'
  | 'running'
  | 'success'
  | 'failure'
  | 'cancelled'
  | 'skipped';

export type BatchStatus = 'preview' | 'running' | 'done' | 'failed' | 'aborted';

export interface RepoProgress {
  state: RepoState;
  startedAt?: number;
  finishedAt?: number;
  runId?: number;
  runUrl?: string;
  /** Owner login for the run — needed to cancel it via the backend. */
  owner?: string;
  message?: string;
  /** Wall-clock ms when WE dispatched this repo for the CURRENT batch. The poll
   *  loop ignores any GitHub run that started before this (previous run's tail). */
  dispatchedAt?: number;
}

export interface DeployPlanEntry {
  repoName: string;
  productId: string;
  displayName?: string;
  category: string;
  deployLocked?: boolean;
}

export interface DeployPlanPhase {
  phase: number;
  name: string;
  description: string;
  repos: DeployPlanEntry[];
}

export interface DeployPlan {
  environment: 'dev' | 'prod';
  product?: string;
  phases: DeployPlanPhase[];
  totalRepos: number;
  skippedRepos: DeployPlanEntry[];
}

export interface DeployBatch {
  id: string;
  environment: 'dev' | 'prod';
  product: string;
  org: string;
  status: BatchStatus;
  currentPhaseIdx: number;
  plan: DeployPlan | null;
  progress: Record<string, RepoProgress>;
  batchStartedAt: number | null;
  updatedAt: number;
}

// A batch this old (or older) is discarded on load — we don't rehydrate ancient runs.
export const STALE_MS = 12 * 60 * 60 * 1000; // 12h
export const STORAGE_KEY = 'deployAll:v1';
export const STORAGE_VERSION = 1;

const TERMINAL: RepoState[] = ['success', 'failure', 'cancelled', 'skipped'];
// Terminal states that halt a phase (require the user to Skip/Rerun to proceed).
const BLOCKING: RepoState[] = ['failure', 'cancelled'];
const IN_FLIGHT: RepoState[] = ['dispatching', 'queued', 'running'];

export function isTerminal(state: RepoState): boolean {
  return TERMINAL.includes(state);
}

export function isBlocking(state: RepoState): boolean {
  return BLOCKING.includes(state);
}

export function isInFlight(state: RepoState): boolean {
  return IN_FLIGHT.includes(state);
}

/** True if any repo is mid-flight — used to keep the poll loop alive even after
 *  the batch itself reached a terminal status (e.g. a per-repo rerun after done). */
export function hasInFlight(progress: Record<string, RepoProgress>): boolean {
  return Object.values(progress).some(p => isInFlight(p.state));
}

/** Map a backend webhook run status → RepoState (null = leave unchanged). */
export function statusToState(status?: string, conclusion?: string | null): RepoState | null {
  const s = (status || '').toLowerCase();
  const c = (conclusion || '').toLowerCase();
  if (s === 'success' || c === 'success') return 'success';
  if (s === 'cancelled' || c === 'cancelled') return 'cancelled';
  if (s === 'failure' || c === 'failure' || s === 'timed_out' || c === 'timed_out') return 'failure';
  if (s === 'in_progress' || s === 'running' || s === 'queued' || s === 'pending' || s === 'waiting') {
    return s === 'queued' || s === 'pending' || s === 'waiting' ? 'queued' : 'running';
  }
  return null;
}

export type PhaseOutcome =
  | { kind: 'in-progress' }
  | { kind: 'blocked' }   // all terminal but at least one blocking (failure/cancelled)
  | { kind: 'complete' }; // all terminal, none blocking

/**
 * Evaluate a phase from current progress. Deploy-locked repos are ignored
 * (they're marked skipped up front). A phase is settled only when every active
 * repo is terminal; blocked if any active repo is in a blocking terminal state.
 */
export function evaluatePhase(
  phase: DeployPlanPhase | undefined,
  progress: Record<string, RepoProgress>,
): PhaseOutcome {
  if (!phase) return { kind: 'in-progress' };
  const active = phase.repos.filter(r => !r.deployLocked);
  const states = active.map(r => progress[r.repoName]?.state || 'pending');
  const allDone = states.every(isTerminal);
  if (!allDone) return { kind: 'in-progress' };
  return states.some(isBlocking) ? { kind: 'blocked' } : { kind: 'complete' };
}

/** Fresh progress map for a plan: every repo pending. */
export function seedProgress(plan: DeployPlan | null): Record<string, RepoProgress> {
  const seed: Record<string, RepoProgress> = {};
  if (!plan) return seed;
  for (const ph of plan.phases) {
    for (const r of ph.repos) seed[r.repoName] = { state: 'pending' };
  }
  return seed;
}

/** Repos in a phase that still need dispatching (pending, not deploy-locked). */
export function pendingReposInPhase(
  phase: DeployPlanPhase | undefined,
  progress: Record<string, RepoProgress>,
): string[] {
  if (!phase) return [];
  return phase.repos
    .filter(r => !r.deployLocked && (progress[r.repoName]?.state || 'pending') === 'pending')
    .map(r => r.repoName);
}

export interface BatchSummary {
  success: number;
  failure: number;
  cancelled: number;
  skipped: number;
  running: number;
  pending: number;
}

export function summarize(progress: Record<string, RepoProgress>): BatchSummary {
  const all = Object.values(progress);
  return {
    success: all.filter(p => p.state === 'success').length,
    failure: all.filter(p => p.state === 'failure').length,
    cancelled: all.filter(p => p.state === 'cancelled').length,
    skipped: all.filter(p => p.state === 'skipped').length,
    running: all.filter(p => isInFlight(p.state)).length,
    pending: all.filter(p => p.state === 'pending').length,
  };
}

export function durationText(start?: number, end?: number, now = Date.now()): string {
  if (!start) return '';
  const ms = (end || now) - start;
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ── Persistence ──────────────────────────────────────────────────────────────

interface StoredEnvelope {
  version: number;
  batch: DeployBatch;
}

export function serializeBatch(batch: DeployBatch): string {
  const envelope: StoredEnvelope = { version: STORAGE_VERSION, batch };
  return JSON.stringify(envelope);
}

/**
 * Parse a persisted batch. Returns null when the payload is missing, malformed,
 * a different version, in a terminal/preview status (nothing to resume), or
 * older than STALE_MS.
 */
export function deserializeBatch(raw: string | null, now = Date.now()): DeployBatch | null {
  if (!raw) return null;
  let env: StoredEnvelope;
  try {
    env = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!env || env.version !== STORAGE_VERSION || !env.batch) return null;
  const batch = env.batch;
  // Only running batches are worth resuming; a terminal one just clutters.
  if (batch.status !== 'running') return null;
  if (!batch.updatedAt || now - batch.updatedAt > STALE_MS) return null;
  return batch;
}
